import { Modal, Spin, message } from 'antd';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Quill } from 'react-quill-new';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQueries from '../../api/useAPIQueries';
import useAPIQuery from '../../api/useAPIQuery';
import EmailTemplateForm, { EmailTemplateFormValues } from '../../components/EmailTemplateForm';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import {
  quillGetHTMLString,
  fixUnorderedListInQuill,
} from '../../components/RichEditor/RichEditor';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { EmailCommunicationTemplateDetail } from '../../types';
import EditEmailTemplateDrawer from './EditEmailTemplateDrawer';

const EditEmailTemplatePage = () => {
  const { tenant, template_id } = useTypedParams({ tenant: String, template_id: Number });
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const typeValue = Form.useWatch('type', form);
  const [setIsDirty] = useDiscardChangesModal(form);
  const navigate = useTypedNavigate();

  const { t } = useTranslation();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const emailCommunicationTemplate = useAPIQuery(`/email-communication-templates/${template_id}/`);

  const attachments = useAPIQueries(
    emailCommunicationTemplate.data?.attachments.map((attachmentId) => [
      `/attachments/${attachmentId}/`,
      {},
    ]) || []
  );

  const stages = useAPIQuery([
    '/stages/',
    {
      params: {
        email_communication_template: Number(template_id),
      },
    },
  ]);

  const editMutation = useAPIMutation(
    async (
      {
        formVariables: {
          name,
          message,
          type,
          fromName,
          subject,
          attachments,
          activeWelcomeTemplate,
        },
        emailCommunicationTemplate,
      }: {
        formVariables: EmailTemplateFormValues;
        emailCommunicationTemplate: EmailCommunicationTemplateDetail;
      },
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        const attachmentId = parseInt(attachment.uid, 10);

        if (emailCommunicationTemplate?.attachments.includes(attachmentId)) {
          attachmentIds.push(attachmentId);
        } else {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        }
      }

      await api.patch(`/email-communication-templates/${template_id}/`, {
        name,
        subject,
        from_name: fromName,
        message,
        type,
        attachments: attachmentIds,
      });

      if (activeWelcomeTemplate && template_id) {
        await api.patch('/configs/tenant-configurations/company-welcome-message-template-id/', {
          value: template_id,
        });
      }
    },
    {
      onError: (error: AxiosError) => {
        if (error?.response?.status === 413) {
          message.error(
            t('O arquivo selecionado excede o limite de 2 MB. Por favor, envie um arquivo menor.')
          );
        } else {
          message.error(t('Erro ao salvar template.'));
        }
      },
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Template atualizado com sucesso'));
        navigate({
          pathname: '/:tenant/settings/emailTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/email-communication-templates/');
        await queryClient.invalidateQueries(`/email-communication-templates/${template_id}/`);
        await queryClient.invalidateQueries(`/configs/tenant-configurations/`);
      },
    }
  );

  const deleteMutation = useAPIMutation(
    async (emailCommunicationTemplate: EmailCommunicationTemplateDetail, api) => {
      await api.delete(`/email-communication-templates/${emailCommunicationTemplate.id}/`);
    },
    {
      onSuccess: async () => {
        message.success(t('Modelo deletado'));
        navigate({
          pathname: '/:tenant/settings/emailTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/email-communication-templates/');
      },
    }
  );

  return (
    <Spin
      spinning={emailCommunicationTemplate.isLoading || attachments.isLoading || stages.isLoading}
    >
      <ErrorResult error={emailCommunicationTemplate.error}>
        <EditEmailTemplateDrawer
          templateName={emailCommunicationTemplate.data?.name}
          loading={editMutation.isLoading || deleteMutation.isLoading}
          onSave={() => form.submit()}
          deleteDisabled={config.data?.companyWelcomeMessageTemplateId === template_id}
          onDelete={() => {
            setIsDirty(false);
            if ((stages.data?.count ?? 0) > 0) {
              return Modal.warning({
                title: t('Atenção!'),
                content: t(
                  'Este template está vinculado a uma vaga e por isso não pode ser excluido. Para remover, primeiro desvincule-o da vaga.'
                ),
              });
            }
            if (emailCommunicationTemplate.data) {
              deleteMutation.mutate(emailCommunicationTemplate.data);
            }
          }}
        >
          <ErrorResult error={emailCommunicationTemplate.error}>
            <Form<EmailTemplateFormValues>
              form={form}
              layout="vertical"
              onFinish={(variables) => {
                if (emailCommunicationTemplate.data) {
                  editMutation.mutate({
                    formVariables: {
                      ...variables,
                      message: fixUnorderedListInQuill(variables.message),
                    },
                    emailCommunicationTemplate: emailCommunicationTemplate.data,
                  });
                }
              }}
              initialValues={{
                name: emailCommunicationTemplate.data?.name,
                message: quillGetHTMLString(emailCommunicationTemplate.data?.message || '') || '',
                type: emailCommunicationTemplate.data?.type,
                fromName: emailCommunicationTemplate.data?.from_name,
                subject: emailCommunicationTemplate.data?.subject,
                activeWelcomeTemplate: config.data?.companyWelcomeMessageTemplateId === template_id,
                attachments: attachments.data?.map((attachment) => ({
                  uid: attachment.id,
                  name: attachment.filename,
                  status: 'done',
                  url: attachment.file,
                  type: attachment.mimetype,
                  fileName: attachment.filename,
                })),
              }}
              onValuesChange={(changedValues) => {
                const keys = Object.keys(changedValues);
                if (keys.length === 1 && keys[0] === 'message') {
                  const tempContainer = document.createElement('div');
                  const editor = new Quill(tempContainer);
                  const initialDelta = editor.clipboard.convert({
                    html: quillGetHTMLString(emailCommunicationTemplate.data?.message || '') || '',
                  });
                  const currentDelta = editor.clipboard.convert({ html: changedValues.message });
                  const diff = initialDelta.diff(currentDelta);
                  if (!diff?.ops?.length) {
                    setIsDirty(false);
                  } else {
                    setIsDirty(true);
                  }
                } else {
                  setIsDirty(form.isFieldsTouched());
                }
              }}
            >
              <EmailTemplateForm
                fromName
                templateTypes={[
                  { value: 'COMMUNICATION', label: t('Comunicação') },
                  { value: 'TESTS', label: t('Testes Mindsight') },
                  { value: 'ADDITIONAL_QUESTIONS', label: t('Perguntas de pré-requisito') },
                  ...(config.data?.enableAlchemerIntegration
                    ? [{ value: 'CUSTOM_TESTS', label: t('Testes Customizados') }]
                    : []),
                  { value: 'WELCOME_MESSAGE', label: t('Mensagem boas-vindas') },
                ]}
                disableTypeField={
                  (stages.data?.count ?? 0) > 0 ||
                  config.data?.companyWelcomeMessageTemplateId === template_id
                }
                disableActiveWelcomeTemplate={
                  config.data?.companyWelcomeMessageTemplateId === template_id
                }
                typeTooltipMessage={
                  (stages.data?.count ?? 0) > 0
                    ? t('Não é possível editar o tipo, pois o template está associado a uma vaga')
                    : ''
                }
                customVariables={[
                  ...(typeValue === 'TESTS'
                    ? [{ value: '[link_testes]', label: t('Link dos testes') }]
                    : []),
                  ...(typeValue === 'ADDITIONAL_QUESTIONS'
                    ? [
                        {
                          value: '[link_perguntas_adicionais]',
                          label: t('Link Perguntas de pré-requisito'),
                        },
                      ]
                    : []),
                  ...(typeValue === 'CUSTOM_TESTS' && config.data?.enableAlchemerIntegration
                    ? [
                        {
                          value: '[link_testes_customizados]',
                          label: t('Link Testes Customizados'),
                        },
                      ]
                    : []),
                  { value: '[candidato]', label: t('Nome do Candidato') },
                  { value: '[empresa]', label: t('Nome da Empresa') },
                  { value: '[vaga]', label: t('Nome da Vaga') },
                ]}
                richEditorRules={[
                  typeValue === 'TESTS'
                    ? {
                        validator: (_, value: string) => {
                          if (
                            value &&
                            (value.includes('[link_testes]') || value.includes('[LINK_TESTS]'))
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            // TODO: TRADUZIR AQ DPS
                            new Error(
                              'Para criar este modelo é necessário incluir a variável Link dos Testes'
                            )
                          );
                        },
                      }
                    : {},
                  typeValue === 'CUSTOM_TESTS' && config.data?.enableAlchemerIntegration
                    ? {
                        validator(_, value) {
                          const variablesRegex = /\[link_testes_customizados\]/;
                          if (!variablesRegex.test(value)) {
                            return Promise.reject(
                              new Error( // TODO: TRADUZIR AQ DPS
                                'Para criar este modelo é necessário incluir a variável Link Testes Customizados'
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }
                    : {},
                  typeValue === 'ADDITIONAL_QUESTIONS'
                    ? {
                        validator(_, value) {
                          const variablesRegex = /\[link_perguntas_adicionais\]/;
                          if (!variablesRegex.test(value)) {
                            return Promise.reject(
                              new Error( // TODO: TRADUZIR AQ DPS
                                'Para criar este modelo é necessário incluir a variável Link Perguntas de pré-requisito'
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }
                    : {},
                  {
                    validator(_, value: string) {
                      const cleanHtmlRegex = /<[^>]*>/g;

                      if (!value.replaceAll(cleanHtmlRegex, '').trim()) {
                        return Promise.reject(new Error('Campo obrigatório.')); // TODO: TRADUZIR AQ DPS
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                image
                attachments
              />
            </Form>
          </ErrorResult>
        </EditEmailTemplateDrawer>
      </ErrorResult>
    </Spin>
  );
};

export default withRouteChange()(EditEmailTemplatePage);
